import i18n from '../../i18n';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { dateSep, datePattern } from '../../lib/dateFormat';

// Date fields that need conversion
export const DATE_FIELDS = [
  'birth_date', 'hire_date', 'start_date', 'contract_date', 
  'id_card_expiry_date', 'driving_licence_expiry', 
  'passport_issue_date', 'passport_expiry_date',
  'a1_expiry_date', 'cr_cert_issue_date',
  'eu_visa_expiry_date', 'us_visa_expiry_date', 'uk_work_permit_expiry_date',
  'work_permit_expiry', 'visa_expiry', 'a1_certificate_expiry'
];

// Fields that employees CANNOT edit (admin only)
export const RESTRICTED_FIELDS = new Set([
  'email', 'hire_date', 'start_date', 'status', 'department', 
  'position_1', 'position_2', 'position_3', 'positions',
  'contract_date', 'contract_number', 'id', 'user_id', 'is_active', 
  'created_at', 'updated_at',
  'hourly_rate_1', 'hourly_rate_2', 'hourly_rate_3',
  'currency_1', 'currency_2', 'currency_3'
]);

export const isRestricted = (field) => RESTRICTED_FIELDS.has(field);

// Reusable DatePicker Component with month/year selectors
export const FormDatePicker = ({ 
  field, 
  label, 
  value, 
  disabled = false, 
  onDateChange,
  showAdminBadge = false,
  labelExtras = null,
}) => {
  const dateValue = value instanceof Date ? value : (value ? new Date(value) : null);
  // Controlled month/year for the Calendar's visible page. Kept in
  // state so that (a) the picker OPENS to the stored date's month
  // rather than "today", and (b) the month/year `<select>`s at the
  // top stay in sync when the user pages via the calendar chevrons.
  const [viewMonth, setViewMonth] = React.useState(dateValue || new Date());

  // Re-sync viewMonth if the parent-supplied value changes (e.g. edit
  // dialog opens on a new employee) — otherwise the stale month from
  // the previous open would linger.
  React.useEffect(() => {
    if (dateValue) setViewMonth(dateValue);
  }, [value?.toString?.() || value]);

  const updateViewMonth = (year, month) => {
    const next = new Date(viewMonth);
    next.setFullYear(year);
    next.setMonth(month);
    // clamp day so that e.g. Jan 31 -> Feb doesn't overflow to Mar 3
    setViewMonth(next);
  };

  return (
    <div className="space-y-2">
      <Label className={disabled ? 'text-slate-500' : ''}>
        <span className="inline-flex items-center gap-1.5">
          {label}
          {labelExtras}
        </span>
        {' '}{showAdminBadge && disabled && <span className="text-xs text-amber-600">{i18n.t('auto.misc2.adminOnly2')}</span>}
      </Label>
      <Popover
        onOpenChange={(open) => {
          // Every time the popover opens, jump the calendar to the
          // stored date's month (or today when unset). Fixes the
          // "picker always shows current month" bug.
          if (open) setViewMonth(dateValue || new Date());
        }}
      >
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className={`w-full justify-start text-left font-normal h-10 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateValue ? format(dateValue, `dd${dateSep()}MM${dateSep()}yyyy`) : datePattern()}
          </Button>
        </PopoverTrigger>
        {!disabled && (
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3">
              <div className="flex justify-center items-center gap-2 mb-4">
                <select
                  value={viewMonth.getMonth()}
                  onChange={(e) => updateViewMonth(viewMonth.getFullYear(), parseInt(e.target.value))}
                  className="text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  value={viewMonth.getFullYear()}
                  onChange={(e) => updateViewMonth(parseInt(e.target.value), viewMonth.getMonth())}
                  className="text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1"
                >
                  {(() => {
                    // Base window: current year ± ~half-century, but always
                    // include the currently-viewed year (handles employees
                    // born 80+ years ago or long-dated future expiries).
                    const cy = new Date().getFullYear();
                    const start = Math.min(cy - 80, viewMonth.getFullYear());
                    const end = Math.max(cy + 20, viewMonth.getFullYear());
                    const years = [];
                    for (let y = start; y <= end; y++) years.push(y);
                    return years.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ));
                  })()}
                </select>
              </div>
              <Calendar
                mode="single"
                selected={dateValue || undefined}
                month={viewMonth}
                onMonthChange={setViewMonth}
                onSelect={(date) => date && onDateChange(field, date)}
                className="rounded-md border-0"
              />
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
};

// Restricted Input (read-only with admin badge)
export const RestrictedInput = ({ label, type = 'text', value, ...props }) => (
  <div>
    <Label className="text-slate-500">{label} <span className="text-xs text-amber-600">{i18n.t('auto.misc2.adminOnly2')}</span></Label>
    <Input 
      type={type}
      value={value || ''} 
      disabled 
      className="opacity-60 cursor-not-allowed" 
      {...props}
    />
  </div>
);

// Restricted Select (read-only with admin badge)
export const RestrictedSelect = ({ label, value, options, placeholder }) => (
  <div>
    <Label className="text-slate-500">{label} <span className="text-xs text-amber-600">{i18n.t('auto.misc2.adminOnly2')}</span></Label>
    <Select value={value || ''} disabled>
      <SelectTrigger className="opacity-60 cursor-not-allowed"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options?.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
      </SelectContent>
    </Select>
  </div>
);

// Certificate Section Component
export const CertificateSection = ({ 
  title, 
  category, 
  certTypes, 
  // Optional Polish caption resolver (see hooks/useValueLabels).
  labelFor = (v) => v,
  isCertificateChecked, 
  getCertificateExpiryDate, 
  onToggle, 
  onExpiryChange,
  disabled = false
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {certTypes && certTypes.map((certType) => {
        const isChecked = isCertificateChecked(category, certType);
        const expiryDate = getCertificateExpiryDate(category, certType);
        
        return (
          <div key={certType} className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${category}-${certType}`}
                checked={isChecked}
                onCheckedChange={(checked) => !disabled && onToggle(category, certType, checked)}
                disabled={disabled}
              />
              <Label htmlFor={`${category}-${certType}`} className={`cursor-pointer ${disabled ? 'text-slate-500' : ''}`}>
                {labelFor(certType)}
              </Label>
            </div>
            
            {isChecked && (
              <div className="ml-6">
                <FormDatePicker
                  field={`${category}-${certType}-expiry`}
                  label={i18n.t('auto.misc2.expiryDate')}
                  value={expiryDate ? new Date(expiryDate) : null}
                  onDateChange={(f, date) => onExpiryChange(category, certType, date)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        );
      })}
    </CardContent>
  </Card>
);

// Form Input with optional disabled state
export const FormInput = ({ 
  label, 
  field, 
  value, 
  onChange, 
  type = 'text', 
  required = false,
  disabled = false,
  showAdminBadge = false,
  ...props 
}) => (
  <div>
    <Label className={disabled ? 'text-slate-500' : ''}>
      {label} {required && '*'} {showAdminBadge && disabled && <span className="text-xs text-amber-600">{i18n.t('auto.misc2.adminOnly2')}</span>}
    </Label>
    <Input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(field, e.target.value)}
      required={required}
      disabled={disabled}
      className={disabled ? 'opacity-60 cursor-not-allowed' : ''}
      {...props}
    />
  </div>
);

// Form Select with optional disabled state
export const FormSelect = ({ 
  label, 
  field, 
  value, 
  onChange, 
  options, 
  placeholder = 'Select...',
  disabled = false,
  showAdminBadge = false 
}) => (
  <div>
    <Label className={disabled ? 'text-slate-500' : ''}>
      {label} {showAdminBadge && disabled && <span className="text-xs text-amber-600">{i18n.t('auto.misc2.adminOnly2')}</span>}
    </Label>
    <Select value={value || ''} onValueChange={(v) => onChange(field, v)} disabled={disabled}>
      <SelectTrigger className={disabled ? 'opacity-60 cursor-not-allowed' : ''}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options?.map(opt => {
          const optValue = typeof opt === 'object' ? opt.value : opt;
          const optLabel = typeof opt === 'object' ? opt.label : opt;
          return <SelectItem key={optValue} value={optValue}>{optLabel}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  </div>
);

// Emergency Contact Section
export const EmergencyContactSection = ({ 
  contact, 
  onChange,
  disabled = false 
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">{i18n.t('auto.misc2.emergencyContact')}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <FormInput
        label={i18n.t('auto.misc2.name')}
        field="name"
        value={contact?.name}
        onChange={(f, v) => onChange(f, v)}
        disabled={disabled}
      />
      <FormInput
        label={i18n.t('auto.misc2.relationship')}
        field="relationship"
        value={contact?.relationship}
        onChange={(f, v) => onChange(f, v)}
        disabled={disabled}
      />
      <FormInput
        label={i18n.t('auto.misc2.phone')}
        field="phone"
        value={contact?.phone}
        onChange={(f, v) => onChange(f, v)}
        disabled={disabled}
      />
    </CardContent>
  </Card>
);

// Helper to convert form data dates
export const convertFormDates = (formData) => {
  const result = { ...formData };
  DATE_FIELDS.forEach(field => {
    if (result[field]) {
      result[field] = new Date(result[field]);
    }
  });
  return result;
};

// Helper to format dates for API
export const formatFormDatesForApi = (formData) => {
  const result = { ...formData };
  DATE_FIELDS.forEach(field => {
    if (result[field] instanceof Date) {
      result[field] = result[field].toISOString();
    }
  });
  return result;
};
